import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { io, Socket } from 'socket.io-client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { getConversationDetails, getCollectionConversationDetails, updateQna, getQnaPairStatus, updateConversationAnnotation } from '../services/api';
import { UniversalMessage } from '../types';
import config from '../config';

interface ConversationDetailData {
  conversation_id: string;
  ai_id: string;
  collection_id: string;
  user_id: string;
  user_identifier: string;
  user_type: string;
  started_at: string;
  total_messages: number;
  messages: UniversalMessage[];
  annotation?: string;
}

const ConversationDetail = () => {
  const { aiId, conversationId, collectionId } = useParams<{ aiId: string; conversationId: string; collectionId?: string }>();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<ConversationDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Inline editing state
  const [editingPair, setEditingPair] = useState<{ messageIndex: number; pairIndex: number } | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  
  // Current Q&A versions cache
  const [currentQnaPairs, setCurrentQnaPairs] = useState<Record<string, any>>({});
  const [loadingCurrentQnaPairs, setLoadingCurrentQnaPairs] = useState<Record<string, boolean>>({});
  
  // Conversation annotation state
  const [annotation, setAnnotation] = useState<string>('');
  const [annotationSaving, setAnnotationSaving] = useState(false);
  const [annotationLastSaved, setAnnotationLastSaved] = useState<Date | null>(null);
  const [annotationError, setAnnotationError] = useState<string | null>(null);
  
  // UI state persistence for accordion expansions
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({});

  // Manual mode state
  const [manualMode, setManualMode] = useState(false);
  const [manualModeLoading, setManualModeLoading] = useState(false);
  const [manualMessage, setManualMessage] = useState('');
  const [liveMessages, setLiveMessages] = useState<any[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isTrainerActive, setIsTrainerActive] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Helper function to truncate text reliably for all content types
  const truncateText = (text: string, maxLength: number = 300) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Define loadConversationDetails before it's used in useEffect
  const loadConversationDetails = useCallback(async () => {
    if (!aiId || !conversationId) return;
    
    try {
      let response;
      if (collectionId) {
        response = await getCollectionConversationDetails(aiId, collectionId, conversationId);
      } else {
        response = await getConversationDetails(aiId, conversationId);
      }
      const conversationData = response.data.data || response.data;
      setConversation(conversationData);
      // Initialize annotation from conversation data
      setAnnotation(conversationData.annotation || '');
    } catch (err) {
      console.error('Error loading conversation details:', err);
      setError('Failed to load conversation details');
    } finally {
      setLoading(false);
    }
  }, [aiId, conversationId, collectionId]);

  useEffect(() => {
    if (!aiId || !conversationId) return;

    const initializeData = async () => {
      await loadConversationDetails();
    };

    // Initialize Socket.io connection
    const socketUrl = config.socketUrl;
    console.log(`🔌 FRONTEND SOCKET: Initializing connection to ${socketUrl} for conversation ${conversationId}`);

    const socketConnection = io(socketUrl);
    setSocket(socketConnection);

    // Socket connection event logging
    socketConnection.on('connect', () => {
      console.log(`🔌 FRONTEND SOCKET CONNECTED: Socket ${socketConnection.id} connected to server`);
    });

    socketConnection.on('connect_error', (error) => {
      console.error(`🔌 FRONTEND SOCKET ERROR: Connection failed:`, error);
    });

    socketConnection.on('disconnect', (reason) => {
      console.log(`🔌 FRONTEND SOCKET DISCONNECTED: Reason: ${reason}`);
    });

    // Join conversation room
    console.log(`👤 FRONTEND JOIN: Attempting to join conversation room for ${conversationId} as trainer`);
    socketConnection.emit('join_conversation', {
      conversationId,
      userType: 'trainer',
      trainerId: 'current_trainer'
    });

    // Listen for new messages from visitors
    socketConnection.on('new_message', (data) => {
      console.log(`📨 FRONTEND NEW_MESSAGE: Received message event:`, data);
      if (data.conversationId === conversationId && data.message.from === 'user') {
        console.log(`👤 FRONTEND VISITOR MESSAGE: Adding visitor message to live chat: "${data.message.text}"`);
        setLiveMessages(prev => [...prev, {
          from: 'user',
          text: data.message.text,
          timestamp: data.message.timestamp
        }]);
      } else {
        console.log(`📨 FRONTEND MESSAGE FILTERED: Message not for this conversation or not from user`, {
          receivedConversationId: data.conversationId,
          expectedConversationId: conversationId,
          messageFrom: data.message?.from
        });
      }
    });

    // Listen for new visitor messages specifically
    socketConnection.on('new_visitor_message', (data) => {
      console.log(`📨 FRONTEND NEW_VISITOR_MESSAGE: Received visitor message event:`, data);
      if (data.conversationId === conversationId) {
        console.log(`👤 FRONTEND VISITOR MESSAGE: Adding visitor message to live chat: "${data.message.text}"`);
        setLiveMessages(prev => [...prev, {
          from: 'user',
          text: data.message.text,
          timestamp: data.message.timestamp
        }]);
      }
    });

    // Listen for live user messages in manual mode
    socketConnection.on('live_user_message', (data) => {
      console.log(`📨 FRONTEND LIVE_USER_MESSAGE: Received live user message event:`, data);
      if (data.conversationId === conversationId) {
        console.log(`👤 FRONTEND LIVE USER MESSAGE: Adding live user message to chat: "${data.message}"`);
        setLiveMessages(prev => [...prev, {
          from: 'user',
          text: data.message,
          timestamp: data.timestamp || new Date().toISOString()
        }]);
      }
    });

    // Listen for manual mode changes
    socketConnection.on('manual_mode_changed', (data) => {
      console.log(`🔧 FRONTEND MANUAL MODE CHANGE: Received mode change event:`, data);
      if (data.conversationId === conversationId) {
        console.log(`🔧 FRONTEND MANUAL MODE: Setting manual mode to ${data.enabled} for conversation ${conversationId}`);
        setManualMode(data.enabled);
      }
    });

    // Listen for trainer connection status
    socketConnection.on('trainer_joined', (data) => {
      console.log(`👨‍🏫 FRONTEND TRAINER JOINED: Trainer joined event:`, data);
      setIsTrainerActive(true);
    });

    socketConnection.on('trainer_disconnected', (data) => {
      console.log(`👨‍🏫 FRONTEND TRAINER DISCONNECTED: Trainer disconnected event:`, data);
      setIsTrainerActive(false);
    });

    initializeData();

    // Cleanup on unmount
    return () => {
      if (socketConnection) {
        socketConnection.emit('leave_conversation', { conversationId });
        socketConnection.disconnect();
      }
    };
  }, [aiId, conversationId, collectionId, loadConversationDetails]);

  // Auto-save annotation with debouncing
  useEffect(() => {
    if (!conversation || annotation === (conversation.annotation || '')) {
      // Don't save if conversation not loaded or annotation unchanged
      return;
    }

    const saveAnnotation = async () => {
      if (!aiId || !conversationId) return;
      
      setAnnotationSaving(true);
      setAnnotationError(null);
      
      try {
        await updateConversationAnnotation(aiId, conversationId, annotation);
        setAnnotationLastSaved(new Date());
      } catch (error) {
        console.error('Error saving annotation:', error);
        setAnnotationError('Failed to save annotation');
      } finally {
        setAnnotationSaving(false);
      }
    };

    // Debounce: wait 1000ms after user stops typing
    const timeoutId = setTimeout(saveAnnotation, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [annotation, aiId, conversationId, conversation]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [liveMessages]);

  // Load conversation status to check if already in manual mode
  useEffect(() => {
    const loadConversationStatus = async () => {
      if (!conversationId) return;

      try {
        const apiUrl = process.env.REACT_APP_API_BASE_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
        const response = await fetch(`${apiUrl}/manual/conversations/${conversationId}/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setManualMode(data.manualMode || false);
        }
      } catch (error) {
        console.error('Error loading conversation status:', error);
      }
    };

    loadConversationStatus();
  }, [conversationId]);


  const handleBackToReview = () => {
    if (collectionId) {
      navigate(`/review/${aiId}/versions/${collectionId}`);
    } else {
      navigate(`/review/${aiId}`);
    }
  };

  // Inline editing functions
  const handleStartEdit = (messageIndex: number, pairIndex: number, pair: any) => {
    setEditingPair({ messageIndex, pairIndex });
    setEditQuestion(pair.question || '');
    setEditAnswer(pair.answer || '');
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingPair(null);
    setEditQuestion('');
    setEditAnswer('');
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingPair || !conversation || !aiId) return;

    const { messageIndex, pairIndex } = editingPair;
    const pair = conversation.messages[messageIndex].qa_pairs_used?.[pairIndex];
    
    if (!pair?.id) {
      setEditError('Cannot edit Q&A pair without ID');
      return;
    }

    const collectionId = conversation.collection_id;
    if (!collectionId) {
      setEditError('Collection ID not available');
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      await updateQna(aiId, collectionId, pair.id, {
        question: editQuestion,
        answer: editAnswer,
        tags: pair.tags || []
      });

      // Refresh the current Q&A pair data without modifying the snapshot
      // This will cause the current database version to be re-fetched
      const qnaId = pair.id;
      if (qnaId) {
        // Remove the cached current version to force a refresh
        setCurrentQnaPairs(prev => {
          const updated = { ...prev };
          delete updated[qnaId];
          return updated;
        });
      }

      handleCancelEdit();
    } catch (error) {
      console.error('Error updating Q&A pair:', error);
      setEditError('Failed to update Q&A pair');
    } finally {
      setEditLoading(false);
    }
  };

  // Helper function to check if Q&A pair has been changed since snapshot
  const hasQnaPairChanged = (pair: any) => {
    if (!pair.lastUpdated || !pair.snapshotTimestamp) return false;
    return new Date(pair.lastUpdated) > new Date(pair.snapshotTimestamp);
  };

  // Helper function to check if current database version differs from snapshot
  const doesDatabaseVersionDiffer = (currentVersion: any, snapshotPair: any) => {
    if (!currentVersion || !snapshotPair) return false;
    
    // Compare question and answer content
    const questionsDiffer = (currentVersion.question || '') !== (snapshotPair.question || '');
    const answersDiffer = (currentVersion.answer || '') !== (snapshotPair.answer || '');
    
    return questionsDiffer || answersDiffer;
  };

  // Helper functions for accordion state management
  const getAccordionKey = (messageId: string, section: string) => `${messageId}-${section}`;
  
  const handleAccordionChange = (accordionKey: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [accordionKey]: isExpanded
    }));
  };

  const isAccordionExpanded = (accordionKey: string) => {
    return expandedAccordions[accordionKey] || false;
  };

  // Function to fetch current version and status of Q&A pair
  const fetchCurrentQnaPair = async (qnaId: string, snapshotTimestamp?: string) => {
    if (!conversation || !aiId || currentQnaPairs[qnaId] || loadingCurrentQnaPairs[qnaId]) {
      return;
    }

    setLoadingCurrentQnaPairs(prev => ({ ...prev, [qnaId]: true }));

    try {
      // Use the new status endpoint to get both current data and edit status
      const response = await getQnaPairStatus(aiId, conversation.collection_id, qnaId, snapshotTimestamp);
      
      setCurrentQnaPairs(prev => ({ 
        ...prev, 
        [qnaId]: {
          ...response.data.current,
          status: response.data.status
        }
      }));
    } catch (error) {
      console.error('Error fetching current Q&A pair status:', error);
      // Set as null to indicate failed fetch
      setCurrentQnaPairs(prev => ({ ...prev, [qnaId]: null }));
    } finally {
      setLoadingCurrentQnaPairs(prev => ({ ...prev, [qnaId]: false }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !conversation) {
    return (
      <Box>
        <Alert severity="error">{error || 'Conversation not found'}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackToReview}
          sx={{ mt: 2 }}
        >
          Back to Review
        </Button>
      </Box>
    );
  }

  return (
    <Box>


      {/* Breadcrumbs and Navigation */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackToReview}
          sx={{ mb: 2 }}
        >
          Back to Review
        </Button>
        
        <Breadcrumbs separator="›" sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href={collectionId ? `/review/${aiId}/versions/${collectionId}` : `/review/${aiId}`}
            onClick={(e) => {
              e.preventDefault();
              handleBackToReview();
            }}
          >
            Review
          </Link>
          <Typography color="text.primary">Conversation Details</Typography>
        </Breadcrumbs>
      </Box>

      {/* Manual Mode Toggle */}
      <Card sx={{ mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e3f2fd' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Manual Response Mode
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Toggle between AI responses and manual trainer responses for this conversation.
              </Typography>

              {/* Persistent Setting Warning */}
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Manual mode settings remain persistent even if you close this page.
                  The conversation will stay in the mode you set until you manually change it.
                </Typography>
              </Alert>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={manualMode}
                  onChange={async (e) => {
                    const enabled = e.target.checked;
                    const toggleTimestamp = Date.now();

                    console.log(`🔧 FRONTEND MANUAL MODE TOGGLE: Attempting to ${enabled ? 'enable' : 'disable'} manual mode`, {
                      conversationId,
                      enabled,
                      currentState: manualMode,
                      socketConnected: !!socket,
                      timestamp: toggleTimestamp
                    });

                    setManualModeLoading(true);

                    try {
                      // Call API to toggle manual mode
                      const apiUrl = process.env.REACT_APP_API_BASE_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
                      const endpoint = `${apiUrl}/manual/conversations/${conversationId}/manual-mode`;

                      console.log(`🌐 FRONTEND TOGGLE API: Sending POST to ${endpoint}`);

                      const response = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: JSON.stringify({ enabled })
                      });

                      console.log(`🌐 FRONTEND TOGGLE RESPONSE: Status ${response.status} ${response.statusText}`);

                      if (response.ok) {
                        const responseData = await response.json();
                        console.log(`✅ FRONTEND TOGGLE SUCCESS: Manual mode ${enabled ? 'enabled' : 'disabled'}`, responseData);

                        setManualMode(enabled);

                        // Join conversation room if enabling manual mode
                        if (enabled && socket) {
                          console.log(`👤 FRONTEND ROOM JOIN: Joining conversation room as trainer`);
                          socket.emit('join_conversation', {
                            conversationId,
                            userType: 'trainer',
                            trainerId: 'current_trainer' // You might want to get this from context/auth
                          });
                          setIsTrainerActive(true);
                          console.log(`👤 FRONTEND TRAINER ACTIVE: Set trainer as active for manual mode`);
                        }
                      } else {
                        const errorText = await response.text();
                        console.error(`❌ FRONTEND TOGGLE ERROR: HTTP ${response.status}:`, errorText);
                        throw new Error(`Failed to toggle manual mode: ${response.status} ${errorText}`);
                      }
                    } catch (error) {
                      console.error(`❌ FRONTEND TOGGLE FAILED: Error toggling manual mode:`, error);
                      console.error(`❌ FRONTEND TOGGLE DETAILS:`, {
                        conversationId,
                        attemptedState: enabled,
                        timestamp: toggleTimestamp,
                        error: error instanceof Error ? error.message : 'Unknown error'
                      });
                      // Revert the switch state on error
                    } finally {
                      setManualModeLoading(false);
                      console.log(`🔧 FRONTEND TOGGLE COMPLETE: Manual mode toggle operation finished`);
                    }
                  }}
                  disabled={manualModeLoading}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {manualMode ? 'Manual Mode' : 'AI Mode'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {manualMode ? 'You are handling responses' : 'AI is handling responses'}
                  </Typography>
                </Box>
              }
              sx={{ ml: 2 }}
            />
          </Box>

          {manualModeLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Updating manual mode...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Manual Chat Interface - Only show when in manual mode */}
      {manualMode && (
        <Card sx={{ mb: 3, border: '2px solid #4caf50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon />
              Live Chat Interface - Manual Mode Active
            </Typography>

            {/* Live Messages */}
            <Box sx={{
              maxHeight: 300,
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              mb: 2,
              p: 1,
              bgcolor: '#fafafa'
            }}>
              {liveMessages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No new messages in this session. Previous conversation messages are shown below.
                </Typography>
              ) : (
                liveMessages.map((msg, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      mb: 2,
                      alignItems: 'flex-start',
                      justifyContent: msg.from === 'user' ? 'flex-start' : 'flex-end'
                    }}
                  >
                    <Box sx={{
                      maxWidth: '70%',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: msg.from === 'user' ? '#e3f2fd' : '#e8f5e9',
                      border: `1px solid ${msg.from === 'user' ? '#2196f3' : '#4caf50'}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {msg.from === 'user' ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                        <Typography variant="caption" color="text.secondary">
                          {msg.from === 'user' ? 'Visitor' : 'Trainer'} • {new Date(msg.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {msg.text}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Manual Response Input */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                multiline
                maxRows={4}
                fullWidth
                placeholder="Type your response to the visitor..."
                value={manualMessage}
                onChange={(e) => setManualMessage(e.target.value)}
                variant="outlined"
                size="small"
                disabled={!isTrainerActive}
                sx={{ flex: 1 }}
              />
              <IconButton
                color="primary"
                onClick={async () => {
                  if (!manualMessage.trim() || !socket) {
                    console.log(`💬 FRONTEND SEND BLOCKED: Message empty or socket not available`, {
                      messageLength: manualMessage.trim().length,
                      socketConnected: !!socket
                    });
                    return;
                  }

                  const sendTimestamp = Date.now();
                  const messageContent = manualMessage.trim();
                  console.log(`💬 FRONTEND SEND START: Attempting to send manual response`, {
                    conversationId,
                    messageLength: messageContent.length,
                    socketId: socket.id,
                    timestamp: sendTimestamp
                  });

                  try {
                    // Send manual response via API
                    const apiUrl = process.env.REACT_APP_API_BASE_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
                    const endpoint = `${apiUrl}/manual/conversations/${conversationId}/manual-response`;

                    console.log(`🌐 FRONTEND API CALL: Sending POST to ${endpoint}`);

                    const response = await fetch(endpoint, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      },
                      body: JSON.stringify({ message: messageContent })
                    });

                    console.log(`🌐 FRONTEND API RESPONSE: Status ${response.status} ${response.statusText}`);

                    if (response.ok) {
                      const responseData = await response.json();
                      console.log(`✅ FRONTEND SEND SUCCESS: Manual response sent successfully`, responseData);

                      // Add to live messages locally
                      setLiveMessages(prev => [...prev, {
                        from: 'trainer',
                        text: messageContent,
                        timestamp: new Date().toISOString()
                      }]);
                      setManualMessage('');

                      console.log(`📝 FRONTEND UI UPDATE: Message added to local chat and input cleared`);
                    } else {
                      const errorText = await response.text();
                      console.error(`❌ FRONTEND API ERROR: HTTP ${response.status}:`, errorText);
                      throw new Error(`Failed to send manual response: ${response.status} ${errorText}`);
                    }
                  } catch (error) {
                    console.error(`❌ FRONTEND SEND ERROR: Error sending manual response:`, error);
                    console.error(`❌ FRONTEND ERROR DETAILS:`, {
                      conversationId,
                      messageLength: messageContent.length,
                      timestamp: sendTimestamp,
                      error: error instanceof Error ? error.message : 'Unknown error'
                    });
                  }
                }}
                disabled={!manualMessage.trim() || !isTrainerActive}
                sx={{ p: 1 }}
              >
                <SendIcon />
              </IconButton>
            </Box>

            {!isTrainerActive && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                Connecting to chat room...
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Conversation Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Conversation with {conversation.user_identifier}
          </Typography>
          {/* Debug: Show full userID */}
          <Typography variant="subtitle2" sx={{ 
            color: 'orange', 
            fontFamily: 'monospace', 
            backgroundColor: '#fff3e0', 
            padding: '4px 8px', 
            borderRadius: 1,
            display: 'inline-block',
            mb: 2,
            fontSize: '0.85rem'
          }}>
            🐛 Debug UserID: {conversation.user_identifier}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Started At
              </Typography>
              <Typography variant="body1">
                {conversation.started_at ? new Date(conversation.started_at).toLocaleString() : 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Total Messages
              </Typography>
              <Typography variant="body1">
                {conversation.total_messages}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                User Type
              </Typography>
              <Chip 
                label={conversation.user_type} 
                size="small" 
                color={conversation.user_type === 'trainer' ? 'primary' : 'default'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Conversation Annotation */}
      <Card sx={{ mt: 4, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Conversation Notes
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Add your notes or annotations about this conversation..."
            value={annotation}
            onChange={(e) => setAnnotation(e.target.value)}
            variant="outlined"
            size="small"
          />
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              {annotationSaving && (
                <Typography variant="caption" color="text.secondary">
                  Saving...
                </Typography>
              )}
              {annotationLastSaved && !annotationSaving && (
                <Typography variant="caption" color="text.secondary">
                  Last saved: {annotationLastSaved.toLocaleTimeString()}
                </Typography>
              )}
              {annotationError && (
                <Typography variant="caption" color="error">
                  Error saving: {annotationError}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Messages - Hidden during manual mode */}
      {!manualMode && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 1 }}>
            Conversation Messages (In Order)
          </Typography>
      
      {/* Snapshotting Explanation */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, borderLeft: '4px solid #2196f3' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>About Q&A Snapshots:</strong> When Q&A pairs are used in conversations, they are "frozen" or snapshotted at that moment in time. 
          This means you see exactly what the AI knew when it responded. If you later edit a Q&A pair, the conversation will show both:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          • <strong>Original Snapshot:</strong> What the AI used during the conversation<br/>
          • <strong>Current Version:</strong> The updated Q&A pair in your database (if different)
        </Typography>
      </Box>
      
      {conversation.messages.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No messages found in this conversation.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {conversation.messages.map((message, idx) => (
            <Paper 
              key={message.id || `message-${idx}`}
              sx={{ 
                mb: 3, 
                p: 3,
                borderLeft: `4px solid ${message.message_type === 'user' ? '#1976d2' : '#4caf50'}`
              }}
            >
              {/* Message Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color={message.message_type === 'user' ? 'primary' : 'success'}>
                  {message.message_type === 'user' ? 'User' : 'AI Assistant'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {message.created_at ? new Date(message.created_at).toLocaleString() : 'Unknown date'}
                  </Typography>
                  {message.rating && (
                    <Chip 
                      label={message.rating} 
                      size="small" 
                      color={message.rating === 'good' ? 'success' : 'error'}
                    />
                  )}
                </Box>
              </Box>

              {/* Message Content */}
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message.content}
              </Typography>

              {/* Q&A Pairs Used */}
              {message.qa_pairs_used && message.qa_pairs_used.length > 0 && (
                <Accordion 
                  sx={{ mt: 2 }}
                  expanded={isAccordionExpanded(getAccordionKey(message.id || `message-${idx}`, 'qna'))}
                  onChange={handleAccordionChange(getAccordionKey(message.id || `message-${idx}`, 'qna'))}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Q&A Pairs Used ({message.qa_pairs_used.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {message.qa_pairs_used.map((pair: any, qIdx: number) => {
                        const isEditing = editingPair?.messageIndex === idx && editingPair?.pairIndex === qIdx;
                        const currentVersion = currentQnaPairs[pair.id];
                        const isLoadingCurrent = loadingCurrentQnaPairs[pair.id];
                        
                        // Use the status from the API if available, otherwise fall back to local logic
                        const hasChanged = currentVersion?.status?.editedSinceSnapshot || hasQnaPairChanged(pair);
                        const hasBeenEdited = currentVersion?.status?.hasBeenEdited;
                        
                        // Fetch current version if we have a Q&A ID and haven't fetched it yet
                        if (pair.id && !currentVersion && !isLoadingCurrent) {
                          fetchCurrentQnaPair(pair.id, pair.snapshotTimestamp || message.created_at);
                        }
                        
                        return (
                          <Box key={qIdx} sx={{ mb: 3 }}>
                            {/* Original Q&A Pair (Snapshot) */}
                            <Card sx={{ mb: hasChanged && currentVersion ? 1 : 2, p: 2, bgcolor: '#f8f9fa' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, maxWidth: '100%' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle2" color="primary">
                                      Question {hasChanged ? '(At Time of Conversation)' : ''}:
                                    </Typography>
                                    {hasChanged && (
                                      <Chip 
                                        icon={<WarningIcon />}
                                        label="Modified since snapshot" 
                                        size="small" 
                                        color="warning" 
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                  {pair.id && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', display: 'block' }}>
                                      ID: {pair.id}
                                    </Typography>
                                  )}
                                  {!pair.id && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', display: 'block' }}>
                                      Conversation Snapshot - No Database ID
                                    </Typography>
                                  )}
                                  {pair.snapshotTimestamp && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                      Snapshotted: {new Date(pair.snapshotTimestamp).toLocaleString()}
                                    </Typography>
                                  )}
                                  {pair.createdAt && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                      Created: {new Date(pair.createdAt).toLocaleString()}
                                    </Typography>
                                  )}
                                </Box>
                                {!isEditing && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleStartEdit(idx, qIdx, pair)}
                                    disabled={!pair.id}
                                    sx={{ minWidth: 'auto', px: 2 }}
                                  >
                                    Edit
                                  </Button>
                                )}
                              </Box>
                              
                              {!isEditing ? (
                                <>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      mb: 2, 
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word'
                                    }}
                                  >
                                    {truncateText(pair.question || 'No question available')}
                                  </Typography>
                                  <Typography variant="subtitle2" color="success.main" gutterBottom>
                                    Answer:
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word'
                                    }}
                                  >
                                    {truncateText(pair.answer || 'No answer available')}
                                  </Typography>
                                </>
                              ) : (
                                <Box sx={{ mt: 2 }}>
                                  {editError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                      {editError}
                                    </Alert>
                                  )}
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Question"
                                    value={editQuestion}
                                    onChange={(e) => setEditQuestion(e.target.value)}
                                    sx={{ mb: 2 }}
                                  />
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Answer"
                                    value={editAnswer}
                                    onChange={(e) => setEditAnswer(e.target.value)}
                                    sx={{ mb: 2 }}
                                  />
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      variant="contained"
                                      startIcon={<SaveIcon />}
                                      onClick={handleSaveEdit}
                                      disabled={editLoading || !editQuestion.trim() || !editAnswer.trim()}
                                    >
                                      {editLoading ? 'Saving...' : 'Save'}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      startIcon={<CloseIcon />}
                                      onClick={handleCancelEdit}
                                      disabled={editLoading}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                            </Card>

                            {/* Current Q&A Pair Database Version - Only show if different from snapshot */}
                            {currentVersion !== undefined && doesDatabaseVersionDiffer(currentVersion, pair) && (
                              <>
                                {isLoadingCurrent && (
                                  <Card sx={{ mb: 2, p: 2, bgcolor: '#f3f4f6', borderLeft: '4px solid #6b7280' }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <CircularProgress size={16} />
                                      <Typography variant="body2" color="text.secondary">
                                        Loading current database version...
                                      </Typography>
                                    </Box>
                                  </Card>
                                )}
                                
                                {currentVersion && currentVersion !== null && (
                                  <Card sx={{ 
                                    mb: 2, 
                                    p: 2, 
                                    bgcolor: hasChanged ? '#e8f5e9' : '#f8f9fa', 
                                    borderLeft: hasChanged ? '4px solid #4caf50' : '4px solid #2196f3' 
                                  }}>
                                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                                      <Typography variant="subtitle2" color={hasChanged ? "success.main" : "primary.main"} gutterBottom>
                                        {hasChanged ? 'Question (Current Database Version):' : 'Question (Database Version):'}
                                      </Typography>
                                      
                                      {hasBeenEdited && (
                                        <Chip 
                                          label="Edited" 
                                          size="small" 
                                          color="info" 
                                          variant="outlined"
                                        />
                                      )}
                                      
                                      {hasChanged && (
                                        <Chip 
                                          label="Changed Since Conversation" 
                                          size="small" 
                                          color="warning" 
                                          variant="filled"
                                        />
                                      )}
                                    </Box>
                                    
                                    <Box sx={{ mb: 2 }}>
                                      {currentVersion.lastUpdated && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                          Last Updated: {new Date(currentVersion.lastUpdated).toLocaleString()}
                                        </Typography>
                                      )}
                                    </Box>
                                    
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        mb: 2, 
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word'
                                      }}
                                    >
                                      {truncateText(currentVersion.question || 'No question available')}
                                    </Typography>
                                    <Typography variant="subtitle2" color={hasChanged ? "success.main" : "primary.main"} gutterBottom>
                                      {hasChanged ? 'Answer (Current Database Version):' : 'Answer (Database Version):'}
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word'
                                      }}
                                    >
                                      {truncateText(currentVersion.answer || 'No answer available')}
                                    </Typography>
                                  </Card>
                                )}
                                
                                {currentVersion === null && (
                                  <Card sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderLeft: '4px solid #f44336' }}>
                                    <Typography variant="body2" color="error">
                                      Could not load current version of this Q&A pair. It may have been deleted.
                                    </Typography>
                                  </Card>
                                )}
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Database Queries Used */}
              {message.database_queries_used && message.database_queries_used.length > 0 && (
                <Accordion 
                  sx={{ mt: 2 }}
                  expanded={isAccordionExpanded(getAccordionKey(message.id || `message-${idx}`, 'db-queries'))}
                  onChange={handleAccordionChange(getAccordionKey(message.id || `message-${idx}`, 'db-queries'))}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Database Queries Used ({message.database_queries_used.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {message.database_queries_used.map((query: any, qIdx: number) => (
                        <Card key={qIdx} sx={{ mb: 2, p: 2, bgcolor: '#f3e5f5' }}>
                          <Typography variant="subtitle2" color="secondary" gutterBottom>
                            Query {qIdx + 1}:
                          </Typography>
                          {query.table && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Table:</strong> {query.table}
                            </Typography>
                          )}
                          {query.query && (
                            <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-word' }}>
                              <strong>Query:</strong> {truncateText(query.query, 300)}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Results:</strong> {Array.isArray(query.results) ? query.results.length : (query.results ? 1 : 0)} records
                          </Typography>
                          {query.results && Array.isArray(query.results) && query.results.length > 0 && (
                            <Box sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
                              <Typography variant="caption" color="text.secondary">
                                {query.results.length <= 10 
                                  ? `Sample Results (showing all ${query.results.length}):`
                                  : `Sample Results (showing first 10 of ${query.results.length} - results trimmed for display):`
                                }
                              </Typography>
                              <Box sx={{ 
                                mt: 1, 
                                fontFamily: 'monospace', 
                                fontSize: '0.875rem', 
                                bgcolor: '#fff', 
                                p: 1, 
                                borderRadius: 1,
                                maxWidth: '100%',
                                overflow: 'auto'
                              }}>
                                <pre style={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-all',
                                  margin: 0,
                                  maxWidth: '100%'
                                }}>{JSON.stringify(query.results.slice(0, 10), null, 2)}</pre>
                              </Box>
                              {query.results.length > 10 && (
                                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                                  ⚠️ {query.results.length - 10} additional records not shown. View complete results in the test page.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Card>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

            </Paper>
          ))}
        </Box>
      )}
        </>
      )}
    </Box>
  );
};

export default ConversationDetail;